import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AttendanceStatusEnum, Card, Pager, ResponseFiltersAttendanceTypes, SalesMonitor, SearchParams } from '../../public-api';
import { AbstractService } from '../commons/abstract.service';
import { AttendanceMessageSearchResult } from '../models/attendance-message-search.model';
import { Attendance, Attendances, CreateAttendanceFromTemplate, MarkAsUnreadResponse, TabulationStatus } from '../models/attendance.model';

@Injectable({ providedIn: 'root' })
export class AttendanceService extends AbstractService<Attendance> {

  getURLBase(): string {
    return '/attendance';
  }

  constructor(private http: HttpClient) {
    super(http);
  }

  preCreate(obj: Attendance): void {
    delete obj?._id;
  }

  preUpdate(obj: Attendance): void {
  }

  getAttendances(idCompany: string, page: number, perPage: number, search?: string, params?: Partial<SearchParams>): Observable<Attendances> {
    const baseURL = `/attendances/${idCompany}`;
    const pager = `${baseURL}?page=${page}&perPage=${perPage}`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        if (search.includes('+')) {
          const searches = search.split('+');
          let urlSearches = '';
          searches.forEach((search) => {
            urlSearches += `&search=${search}`;
          });
          return urlSearches;
        } else {
          return '&search=' + search;
        }
      } else {
        return '';
      }
    }
    const searching = `${pager}${getSearchConfig(search)}`;

    let urling = `${searching}`;
    if (!!params) {
      for (let param in params) {
        urling = `${urling}&${param}=${params[param]}`;
      }
    }
    return super.get(urling);
  }

  createFromTemplate(createAttendanceFromTemplate: CreateAttendanceFromTemplate): Observable<Attendance> {
    return super.post('/create', createAttendanceFromTemplate);
  }

  addRemoveTag(obj: { idTag: string, idAttendance: string }): Observable<Attendance> {
    return super.put(`/addRemoveTag/${obj.idAttendance}`, obj);
  }

  getTabulationByAttendance(idAttendance: string): Observable<Attendance> {
    return super.get(`/tabulation/${idAttendance}`);
  }

  existsContactStatus(contactId: string, statusList: string[], channelId: string): Observable<Boolean> {
    let status = "";
    if (statusList && statusList.length > 0) {
      status = "&status=" + statusList.join("&status=");
    }
    return super.get(`/contacts/${contactId}/exists?v=1${status}&channelId=${channelId}`);
  }

  assignUser(id: string, idUser: string): Observable<Attendance> {
    return super.get(`/assign/user/${id}?idUser=${idUser}`);
  }

  addUpdateSalesMonitor(idAttendance: string, salesMonitor: SalesMonitor): Observable<Attendance> {
    return super.put(`/add/sales-monitor/${idAttendance}`, salesMonitor);
  }

  closeAttendance(idAttendance: string, closeForced: boolean = false, tabulationStatus: TabulationStatus = undefined): Observable<Attendance> {
    return super.put(`/close/attendance/${idAttendance}`, { idAttendance, closeForced, tabulationStatus });
  }

  updateMessagesStatus(idAttendance: string, read: boolean): Observable<void> {
    return super.put(`/up-att/${idAttendance}`, { read })
  }

  transferAttendance(idAttendance: string, idDepartment: string, idUser?: string): Observable<Attendance> {
    return super.put(`/transfer/attendance/${idAttendance}`, { idDepartment, idUser });
  }

  pauseAttendance(idAttendance: string): Observable<Attendance> {
    return super.get(`/pause/attendance/${idAttendance}`);
  }

  continueAttendance(idAttendance: string): Observable<Attendance> {
    return super.get(`/continue/attendance/${idAttendance}`);
  }

  /**
   * "Continue conversation" on an RCS attendance whose 24h service session elapsed
   * (SPEC-2026-08 ADR-003). Distinct from `continueAttendance`, which resumes a PAUSED one.
   */
  continueRcsSession(idAttendance: string): Observable<Attendance> {
    return super.put(`/rcs/continue-session/${idAttendance}`, {});
  }

  getCountAttendancesByContacts(idsContact: string[]): Observable<unknown> {
    return super.post(`/count/attendances/by/contacts`, { ids: idsContact });
  }

  getCountAttendancesByStatus(search?: string, params?: Partial<SearchParams>): Observable<Array<{ status: string, count: number }>> {
    const baseUrl: string = '/count/attendances/by/status';

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        if (search.includes('+')) {
          const searches = search.split('+');
          return searches.map(s => `search=${s}`).join('&');
        } else {
          return `search=${search}`;
        }
      } else {
        return '';
      }
    };

    const searchQuery = getSearchConfig(search);
    let urling = `${baseUrl}${searchQuery ? '?' + searchQuery : ''}`;

    if (params) {
      for (let param in params) {
        urling = `${urling}${urling.includes('?') ? '&' : '?'}${param}=${params[param]}`;
      }
    }

    return super.get(urling);
  }

  updateCountUnreadMessages(idAttendance: string): Observable<void> {
    return super.get('/update/count/unread/messages/' + idAttendance);
  }

  /** POST /attendance/:idAttendance/unread — marks the conversation unread and lets the Panel know (SPEC-2026-10-attendance-tabs-unread-messages RF-13). */
  markAsUnread(idAttendance: string): Observable<MarkAsUnreadResponse> {
    return super.post(`/${idAttendance}/unread`, {});
  }

  /** GET /attendance/search/messages — empty values dropped so HttpParams never sends "undefined". */
  searchMessages(params: Record<string, unknown>): Observable<AttendanceMessageSearchResult> {
    const query: Record<string, string> = {};
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && String(value) !== '') {
        query[key] = String(value);
      }
    });
    return super.getWithParams('/search/messages', query);
  }

  getAttendanceHistory(page: number, perPage: number, search?: string, params?: Partial<SearchParams>): Observable<Pager<Attendance>> {
    const baseURL = `/pager/history`;
    const pager = `${baseURL}?page=${page}&perPage=${perPage}`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        if (search.includes('+')) {
          const searches = search.split('+');
          let urlSearches = '';
          searches.forEach((search) => {
            urlSearches += `&search=${search}`;
          });
          return urlSearches;
        } else {
          return '&search=' + search;
        }
      } else {
        return '';
      }
    }
    const searching = `${pager}${getSearchConfig(search)}`;

    let urling = `${searching}`;
    if (!!params) {
      for (let param in params) {
        urling = `${urling}&${param}=${params[param]}`;
      }
    }
    return super.get(urling);
  }

  activeUnactiveAI(idAttendance: string, actived: boolean, integration?: string, assistant?: string): Observable<Attendance> {
    return super.put(`/activeUnactive/integration/AI/${idAttendance}`, { actived, integration, assistant });
  }

  getPagerToAttendance(page: number, perPage: number, search?: string, params?: Partial<SearchParams>): Observable<Pager<Attendance>> {
    const baseURL = `/pager/attendance`;
    const pager = `${baseURL}?page=${page}&perPage=${perPage}`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        if (search.includes('+')) {
          const searches = search.split('+');
          let urlSearches = '';
          searches.forEach((search) => {
            urlSearches += `&search=${search}`;
          });
          return urlSearches;
        } else {
          return '&search=' + search;
        }
      } else {
        return '';
      }
    }
    const searching = `${pager}${getSearchConfig(search)}`;

    let urling = `${searching}`;
    if (!!params) {
      for (let param in params) {
        urling = `${urling}&${param}=${params[param]}`;
      }
    }
    return super.get(urling);
  }

  getReportCSAT(forms: ResponseFiltersAttendanceTypes): Observable<unknown> {
    const params = new URLSearchParams({
      dateStartCurrent: forms.dateStartCurrent,
      dateEndCurrent: forms.dateEndCurrent,
      department: forms.department,
      attendant: forms.attendant,
      channel: forms.channel,
      feedbackCSAT: forms.feedbackCSAT,
      page: forms.page,
    });
    return super.get(`/report/csat?${params.toString()}`);
  }

  getReportAttendances(page: number, perPage: number, search?: string, params?: Partial<SearchParams>): Observable<Pager<Attendance>> {
    const baseURL = `/report/attendances`;
    const pager = `${baseURL}?page=${page}&perPage=${perPage}`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        return '&search=' + encodeURIComponent(search);
      } else {
        return '';
      }
    }
    const searching = `${pager}${getSearchConfig(search)}`;

    let urling = `${searching}`;
    if (!!params) {
      for (let param in params) {
        urling = `${urling}&${param}=${params[param]}`;
      }
    }
    return super.get(urling);
  }

  getReportAttendancesTabulations(page: number, perPage: number, search?: string, params?: Partial<SearchParams>): Observable<Pager<Attendance>> {
    const baseURL = `/report/attendances-tabulation`;
    const pager = `${baseURL}?page=${page}&perPage=${perPage}`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        return '&search=' + encodeURIComponent(search);
      } else {
        return '';
      }
    }
    const searching = `${pager}${getSearchConfig(search)}`;

    let urling = `${searching}`;
    if (!!params) {
      for (let param in params) {
        urling = `${urling}&${param}=${params[param]}`;
      }
    }
    return super.get(urling);
  }

  getReportAttendancesSalesMonitor(page: number, perPage: number, search?: string, params?: Partial<SearchParams>): Observable<Pager<Attendance>> {
    const baseURL = `/report/attendances-sales-monitor`;
    const pager = `${baseURL}?page=${page}&perPage=${perPage}`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        return '&search=' + encodeURIComponent(search);
      } else {
        return '';
      }
    }
    const searching = `${pager}${getSearchConfig(search)}`;

    let urling = `${searching}`;
    if (!!params) {
      for (let param in params) {
        urling = `${urling}&${param}=${params[param]}`;
      }
    }
    return super.get(urling);
  }

  getReportAttendancesCounts(search?: string, params?: Partial<SearchParams>): Observable<Array<{ status: string, count: number }>> {
    const baseURL = `/report/attendances/counts`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        if (search.includes('+')) {
          const searches = search.split('+');
          return searches.map(s => `search=${s}`).join('&');
        } else {
          return `search=${search}`;
        }
      } else {
        return '';
      }
    };

    const searchQuery = getSearchConfig(search);
    let urling = `${baseURL}${searchQuery ? '?' + searchQuery : ''}`;

    if (params) {
      for (let param in params) {
        urling = `${urling}${urling.includes('?') ? '&' : '?'}${param}=${params[param]}`;
      }
    }

    return super.get(urling);
  }

  getSalesMonitorKPIs(search?: string, params?: Partial<SearchParams>): Observable<Array<{ status: string, count: number }>> {
    const baseURL = `/report/sales-monitor/kpis`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        if (search.includes('+')) {
          const searches = search.split('+');
          return searches.map(s => `search=${s}`).join('&');
        } else {
          return `search=${search}`;
        }
      } else {
        return '';
      }
    };

    const searchQuery = getSearchConfig(search);
    let urling = `${baseURL}${searchQuery ? '?' + searchQuery : ''}`;

    if (params) {
      for (let param in params) {
        urling = `${urling}${urling.includes('?') ? '&' : '?'}${param}=${params[param]}`;
      }
    }

    return super.get(urling);
  }

  pinnedAttendance(idAttendance: string, status: string): Observable<void> {
    return super.get(`/pinned/attendance/${idAttendance}/${status}`);
  }

  unpinnedAttendance(idAttendance: string, status: string): Observable<void> {
    return super.get(`/unpinned/attendance/${idAttendance}/${status}`);
  }

  getFixedAttendances(status: string): Observable<Array<Attendance>> {
    return super.get(`/fixed/attendances/${status}`);
  }

  getMonitoringSummary(params: { channel?: string; threshold: number }): Observable<{ total: number; delayed: number }> {
    let url = `/monitoring/summary?threshold=${params.threshold}`;
    if (params.channel) url += `&channel=${params.channel}`;
    return super.get(url);
  }

  getMonitoringAttendances(page: number, perPage: number, search?: string, params?: Partial<SearchParams>): Observable<Pager<Attendance>> {
    const baseURL = '/monitoring';
    const pager = `${baseURL}?page=${page}&perPage=${perPage}`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        if (search.includes('+')) {
          const searches = search.split('+');
          let urlSearches = '';
          searches.forEach((search) => {
            urlSearches += `&search=${search}`;
          });
          return urlSearches;
        } else {
          return '&search=' + encodeURIComponent(search);
        }
      } else {
        return '';
      }
    }
    const searching = `${pager}${getSearchConfig(search)}`;

    let urling = `${searching}`;
    if (!!params) {
      for (let param in params) {
        urling = `${urling}&${param}=${params[param]}`;
      }
    }
    return super.get(urling);
  }

}
